import KeyboardCapslockRounded from "@mui/icons-material/KeyboardCapslockRounded";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";
import { useState, type FormEvent, type KeyboardEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

import { errorMessage } from "@/shared/api/client";
import { Logo } from "@/shared/components/Logo";

import { useLogin, useMe } from "./api";

/** 主题主色按比例透明化；用 color-mix，明暗两套配色下都取当前主色。 */
const tint = (color: string, percent: number) => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

const enter = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(.99); }
  to { opacity: 1; transform: none; }
`;

const shake = keyframes`
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
`;

export function LoginPage() {
  const me = useMe();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [failures, setFailures] = useState(0);

  if (me.data) return <Navigate to={from} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate(
      { username: username.trim(), password },
      { onSuccess: () => navigate(from, { replace: true }), onError: () => setFailures((n) => n + 1) },
    );
  };
  const detectCaps = (e: KeyboardEvent) => setCapsLock(e.getModifierState("CapsLock"));

  return (
    <Box
      sx={(t) => ({
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: "background.default",
        backgroundImage: `radial-gradient(60rem 40rem at 15% -10%, ${tint((t.vars ?? t).palette.primary.main, 16)}, transparent 60%),
          radial-gradient(50rem 36rem at 110% 110%, ${tint((t.vars ?? t).palette.primary.main, 12)}, transparent 60%)`,
      })}
    >
      <Paper
        key={failures}
        component="form"
        onSubmit={submit}
        noValidate
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 400,
          p: { xs: 3.5, sm: 5 },
          borderRadius: 4,
          boxShadow: "0 24px 60px -24px rgba(15, 23, 42, .25)",
          animation: failures ? `${shake} .4s ease-in-out` : `${enter} .45s ease-out both`,
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: "22px",
                display: "grid",
                placeItems: "center",
                color: "primary.contrastText",
                background: (t) =>
                  `linear-gradient(140deg, ${(t.vars ?? t).palette.primary.light}, ${(t.vars ?? t).palette.primary.dark})`,
                boxShadow: (t) => `0 10px 24px -8px ${tint((t.vars ?? t).palette.primary.main, 60)}`,
              }}
            >
              <Logo size={42} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 750, letterSpacing: -0.4 }}>
              Foxconn Center
            </Typography>
          </Stack>

          <Collapse in={login.isError} unmountOnExit>
            <Alert severity="error">{errorMessage(login.error)}</Alert>
          </Collapse>

          <Stack spacing={2}>
            <TextField
              label="用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              fullWidth
            />
            <Box>
              <TextField
                label="密码"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={detectCaps}
                onKeyDown={detectCaps}
                onBlur={() => setCapsLock(false)}
                autoComplete="current-password"
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShow((v) => !v)}
                          edge="end"
                          tabIndex={-1}
                          aria-label={show ? "隐藏密码" : "显示密码"}
                        >
                          {show ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Collapse in={capsLock}>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "warning.main", mt: 0.75, ml: 0.5 }}>
                  <KeyboardCapslockRounded sx={{ fontSize: 16 }} />
                  <Typography variant="caption">大写锁定已开启</Typography>
                </Stack>
              </Collapse>
            </Box>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={login.isPending || !username.trim() || !password}
            sx={{ py: 1.4, borderRadius: 2.5, fontSize: 16 }}
          >
            {login.isPending ? <CircularProgress size={24} color="inherit" /> : "登录"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
